import React, { useEffect, useState } from 'react';
import axiosInstance from '../../api/axiosConfig';
import './DeliveryAlertSettings.css';

// Who gets woken up (live tab + OS push with sound) when a delivery starts /
// its proof is uploaded. system_admin only — see
// backend_reception/src/models/RealtimeNotifyConfig.js for why a salesman can
// never be a target here: push and the live socket both require a logged-in
// PrimeOffice account, and salesmen aren't users of this app. They keep
// getting the email regardless of what's set here.
const EVENTS = [
    { key: 'delivery-started', label: 'Delivery started', hint: 'Fires the moment a delivery boy taps Start.' },
    { key: 'delivery-proof-uploaded', label: 'Proof of delivery uploaded', hint: 'Fires when the proof photo(s) are filed.' }
];

const EMPTY = { enabled: true, targetBranchManagers: true, targetCardHolders: false, cardHolderCard: 'delivery-management' };

const DeliveryAlertSettings = () => {
    const [config, setConfig] = useState({ started: EMPTY, proof: EMPTY });
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await axiosInstance.get('/api/realtime/delivery-notify-config');
            if (data?.success) setConfig(data.data);
        } catch (error) {
            console.error('Error loading delivery alert settings:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async (key, field, current, patch) => {
        // Optimistic — the toggle should feel instant; a failure below reverts it.
        const next = { ...current, ...patch };
        setConfig((prev) => ({ ...prev, [field]: next }));
        setSavingKey(key);
        try {
            await axiosInstance.put(`/api/realtime/delivery-notify-config/${key}`, patch);
        } catch (error) {
            console.error('Error saving delivery alert settings:', error);
            setConfig((prev) => ({ ...prev, [field]: current }));
            alert('Could not save that change. Please try again.');
        } finally {
            setSavingKey(null);
        }
    };

    if (loading) {
        return <div className="das-loading">Loading alert settings…</div>;
    }

    return (
        <div className="das-container">
            <div className="das-intro">
                <h2>Live delivery alerts</h2>
                <p>
                    Who hears an instant, sound-on notification when a delivery starts or its proof is uploaded —
                    on the live app tab and as a phone push, even with the app closed. This is separate from the
                    email, which goes to the customer's salesman alone and cannot be turned off here.
                </p>
            </div>

            {EVENTS.map(({ key, label, hint }) => {
                const field = key === 'delivery-started' ? 'started' : 'proof';
                const cfg = config[field] || EMPTY;
                const busy = savingKey === key;
                return (
                    <div key={key} className="das-card">
                        <div className="das-card-header">
                            <div>
                                <div className="das-card-title">{label}</div>
                                <div className="das-card-hint">{hint}</div>
                            </div>
                            <label className="das-switch">
                                <input
                                    type="checkbox"
                                    checked={cfg.enabled}
                                    disabled={busy}
                                    onChange={(e) => save(key, field, cfg, { enabled: e.target.checked })}
                                />
                                <span className="das-switch-track"><span className="das-switch-thumb" /></span>
                            </label>
                        </div>

                        <div className={`das-card-body ${cfg.enabled ? '' : 'das-card-body--disabled'}`}>
                            <label className="das-option">
                                <input
                                    type="checkbox"
                                    checked={cfg.targetBranchManagers}
                                    disabled={busy || !cfg.enabled}
                                    onChange={(e) => save(key, field, cfg, { targetBranchManagers: e.target.checked })}
                                />
                                <span>
                                    <b>Branch managers</b> — the manager(s) of that delivery's branch. They are not
                                    copied on the email, so this is the only way they hear about it. Recommended
                                    default.
                                </span>
                            </label>
                            <label className="das-option">
                                <input
                                    type="checkbox"
                                    checked={cfg.targetCardHolders}
                                    disabled={busy || !cfg.enabled}
                                    onChange={(e) => save(key, field, cfg, { targetCardHolders: e.target.checked })}
                                />
                                <span>
                                    <b>Everyone holding the Delivery Management card</b> — a wider net, e.g. a
                                    dispatch/reception screen that should hear every delivery in the group, not
                                    just their own branch.
                                </span>
                            </label>
                        </div>
                    </div>
                );
            })}

            <p className="das-footnote">
                A salesman can never be a target here — push and the live alert both require a signed-in
                PrimeOffice account, and salesmen aren't users of this app. They keep getting the email.
            </p>
        </div>
    );
};

export default DeliveryAlertSettings;
